/**
 * The region drawn from above, one pixel to the tile.
 *
 * Three things want this picture: the minimap in the corner, the whole island
 * on one screen, and the editor's own map. Drawing it three times would mean
 * three answers to what colour a shore is, and the first time somebody paints a
 * path and it shows on one map but not the others, the maps stop being trusted.
 *
 * One pixel to the tile is deliberate. Everything that draws this scales it up
 * with smoothing off, which is both cheap and what a map of a tiled world is
 * supposed to look like: a tile is a square, and a square is what is drawn.
 */

import { CHUNK_TILES } from '@inverse/common';

/**
 * How many steps of height a chart tells apart, and what its top and bottom
 * stand for. The world writes the same numbers when it draws one.
 */
const CHART_LEVELS = 64;
const CHART_FLOOR = -10;
const CHART_CEILING = 100;

/** The top bit of a reading, which says the block is under water. */
const CHART_WET = 0x80;
import type { LocalRegion } from '../game/world-state';

const WATER = 1 << 6;

/** The one painted ground that is a liquid of its own rather than water. */
const LAVA = 8;
const BLOCKED = 1 << 0;

/** What painted ground looks like from above, by the number the tile carries. */
const PAINTED: Readonly<Record<number, readonly [number, number, number]>> = {
  1: [74, 107, 58],
  2: [92, 76, 52],
  3: [169, 146, 106],
  4: [106, 101, 96],
  5: [122, 106, 82],
  6: [223, 230, 234],
  7: [59, 55, 51],
  8: [122, 56, 38],
  9: [138, 133, 120],
  10: [87, 74, 56],
};

/**
 * The colour of one tile, given its flags, its height and what it is painted.
 *
 * Height does most of the work: below nought is water however it is flagged,
 * just above it is sand, and the greens lighten as the ground rises, so the
 * hills read as hills without any shading.
 */
function colourOf(flags: number, height: number, painted: number): [number, number, number] {
  let red = 74;
  let green = 107;
  let blue = 58;

  // A liquid that is not water is drawn as itself. Lava is a liquid and carries
  // the same flag water does, so the map painted the crater at Broadspire as a
  // lake: a hundred tiles of sea blue in the mouth of a volcano.
  const molten = PAINTED[painted];
  if (painted === LAVA && molten) {
    return [molten[0], molten[1], molten[2]];
  }

  if ((flags & WATER) !== 0 || height <= 0) {
    const depth = Math.min(Math.max(-height / 5, 0), 1);
    return [60 - depth * 30, 120 - depth * 60, 140 - depth * 50];
  }

  // The bands a map is drawn in. They used to run out at five tiles above the
  // sea, which on a continent whose plains sit at twenty made the whole of it
  // one grey: everything above the beach was mountain as far as the map knew.
  if (height < 1) {
    red = 169;
    green = 146;
    blue = 106;
  } else if (height < 16) {
    // The low country, greens.
    const rise = (height - 1) / 15;
    red = 74 + rise * 34;
    green = 112 + rise * 12;
    blue = 58 + rise * 6;
  } else if (height < 34) {
    // Upland, going brown.
    const rise = (height - 16) / 18;
    red = 108 + rise * 24;
    green = 124 - rise * 16;
    blue = 64 + rise * 6;
  } else if (height < 58) {
    // Rock.
    const rise = (height - 34) / 24;
    red = 132 + rise * 20;
    green = 108 + rise * 22;
    blue = 70 + rise * 34;
  } else {
    // And snow on the tops, whatever the ground under it is called.
    const rise = Math.min(1, (height - 58) / 20);
    red = 152 + rise * 78;
    green = 130 + rise * 100;
    blue = 104 + rise * 130;
  }

  // Painted ground shows from above, or a path laid across a field would be
  // invisible in the one view it was laid for.
  const made = PAINTED[painted];
  if (painted !== 0 && made) {
    [red, green, blue] = [made[0], made[1], made[2]];
  }

  // Anything standing on a tile darkens it a little, which is what makes a wood
  // read as a wood at this size.
  if ((flags & BLOCKED) !== 0) {
    red *= 0.78;
    green *= 0.78;
    blue *= 0.78;
  }

  return [red, green, blue];
}

/**
 * How much a slope brightens or darkens a tile on the map.
 *
 * A map painted from height bands alone is a map of bands: the whole middle of
 * a continent comes out one green because nearly all of it is between a metre
 * and five. What a map wants is the shape of the ground, and the shape is in
 * the slope rather than in the height. So every tile is lit from the north
 * west, the way every printed map has been for two hundred years, and the downs
 * turn into downs.
 */
function shadeOf(west: number, east: number, north: number, south: number): number {
  // The gradient across the tile, in heights per tile.
  const alongX = (east - west) / 2;
  const alongZ = (south - north) / 2;

  // Lit from the north west: north is negative z, west is negative x, so the
  // slopes facing that way catch the light and the ones facing away lose it.
  const lit = (-alongX - alongZ) / Math.sqrt(1 + alongX * alongX + alongZ * alongZ);

  return Math.max(0.64, Math.min(1.22, 1 + lit * 0.5));
}

/**
 * The whole region as a canvas, one pixel to the tile, laid out as it is seen.
 *
 * North is up and east is to the right, which is what everybody means by those
 * words, and the world is laid out to agree: north is negative z and east is
 * positive x. A canvas's y grows downward and so does south, so a tile lands on
 * the pixel with its own numbers and neither axis is turned round. This used to
 * flip both, to match a camera that rested facing the other way.
 */
export interface Painted {
  canvas: HTMLCanvasElement;
  /** Which part of the region it is a picture of, after clamping. */
  tx: number;
  tz: number;
  width: number;
  height: number;
  /** How many pixels of that canvas one tile takes up. */
  perTile: number;
}

export function paintRegion(
  region: LocalRegion,
  from?: { tx: number; tz: number; width: number; height: number },
  /**
   * How many pixels to give each tile.
   *
   * One for a map of a continent, where a tile is smaller than a pixel anyway.
   * More for the corner map, which shows thirty tiles across a couple of
   * hundred pixels: at one pixel a tile it was drawn as blocks six pixels
   * square, and a map of blocks is a map nobody can read the shape of. The
   * extra pixels are not invented, they carry the slope read at that point
   * rather than at the middle of the tile, so a hillside comes out as a
   * hillside instead of as a staircase.
   */
  perTile = 1,
): Painted {
  // Only the part being looked at. A continent is four million tiles, and
  // building a picture of all of them every second and a half is how a walk
  // across one comes to drop a frame every few paces.
  const stretch = {
    tx: Math.max(0, Math.min(region.width - 1, from?.tx ?? 0)),
    tz: Math.max(0, Math.min(region.height - 1, from?.tz ?? 0)),
    width: from?.width ?? region.width,
    height: from?.height ?? region.height,
  };
  stretch.width = Math.max(1, Math.min(region.width - stretch.tx, stretch.width));
  stretch.height = Math.max(1, Math.min(region.height - stretch.tz, stretch.height));

  const across = Math.max(1, Math.round(perTile));

  const canvas = document.createElement('canvas');
  canvas.width = stretch.width * across;
  canvas.height = stretch.height * across;

  const painted: Painted = { canvas, ...stretch, perTile: across };

  const context = canvas.getContext('2d');
  if (!context) return painted;

  const image = context.createImageData(canvas.width, canvas.height);
  const stride = region.width + 1;
  const { chart } = region;

  /** The ground at any point, from the four corners round it. */
  const heightAt = (atX: number, atZ: number): number => {
    const x0 = Math.floor(atX);
    const z0 = Math.floor(atZ);
    const fx = atX - x0;
    const fz = atZ - z0;

    const a = region.heights[z0 * stride + x0] ?? 0;
    const b = region.heights[z0 * stride + x0 + 1] ?? 0;
    const c = region.heights[(z0 + 1) * stride + x0] ?? 0;
    const d = region.heights[(z0 + 1) * stride + x0 + 1] ?? 0;

    return (a + (b - a) * fx) * (1 - fz) + (c + (d - c) * fx) * fz;
  };

  for (let row = 0; row < canvas.height; row++) {
    const tz = stretch.tz + Math.floor(row / across);
    const intoZ = (row % across) / across;

    for (let column = 0; column < canvas.width; column++) {
      const tx = stretch.tx + Math.floor(column / across);
      const intoX = (column % across) / across;
      let red: number;
      let green: number;
      let blue: number;

      if (region.squares.has(`${tx - (tx % CHUNK_TILES)}.${tz - (tz % CHUNK_TILES)}`)) {
        const height =
          ((region.heights[tz * stride + tx] ?? 0) +
            (region.heights[tz * stride + tx + 1] ?? 0) +
            (region.heights[(tz + 1) * stride + tx] ?? 0) +
            (region.heights[(tz + 1) * stride + tx + 1] ?? 0)) /
          4;

        [red, green, blue] = colourOf(
          region.flags[tz * region.width + tx] ?? 0,
          height,
          region.ground?.[tz * region.width + tx] ?? 0,
        );

        // The shape of the ground, read at this point rather than at the middle
        // of the tile, so a slope drawn across several pixels is a slope.
        if (height > 0) {
          const atX = tx + intoX;
          const atZ = tz + intoZ;
          const shade = shadeOf(
            heightAt(atX - 0.5, atZ),
            heightAt(atX + 1.5, atZ),
            heightAt(atX, atZ - 0.5),
            heightAt(atX, atZ + 1.5),
          );
          red *= shade;
          green *= shade;
          blue *= shade;
        }
      } else {
        // Ground that has not arrived, drawn from the chart the region came
        // with. Coarser, and the difference shows as a slight blockiness where
        // the detail runs out, which is a fair price for a map of a continent
        // somebody has only walked a mile of.
        const bx = Math.min(chart.width - 1, Math.floor(tx / chart.step));
        const bz = Math.min(chart.height - 1, Math.floor(tz / chart.step));
        const at = bz * chart.width + bx;

        const kind = chart.kinds[at] ?? 0;
        const height =
          ((chart.heights[at] ?? 0) / (CHART_LEVELS - 1)) * (CHART_CEILING - CHART_FLOOR) +
          CHART_FLOOR;

        // What a wet tile is painted is dropped, so a sandy seabed reads as sea
        // rather than as a beach under it. Lava is the exception, because lava
        // is the paint and the liquid at once.
        const made = kind & 0x0f;
        const under = (kind & CHART_WET) !== 0;

        [red, green, blue] = colourOf(under ? WATER : 0, height, under && made !== LAVA ? 0 : made);

        // The same shape, read off the chart, which is coarser but is what
        // there is until the ground itself arrives.
        if (height > 0) {
          // Read in tiles rather than in readings, since a chart reading covers
          // several tiles and a slope is a rise over a distance.
          const reading = (atZ: number, atX: number): number =>
            (((chart.heights[atZ * chart.width + atX] ?? 0) / (CHART_LEVELS - 1)) *
              (CHART_CEILING - CHART_FLOOR) +
              CHART_FLOOR) /
            chart.step;

          const shade = shadeOf(
            reading(bz, Math.max(0, bx - 1)),
            reading(bz, Math.min(chart.width - 1, bx + 1)),
            reading(Math.max(0, bz - 1), bx),
            reading(Math.min(chart.height - 1, bz + 1), bx),
          );
          red *= shade;
          green *= shade;
          blue *= shade;
        }
      }

      const at = (row * canvas.width + column) * 4;
      image.data[at] = red;
      image.data[at + 1] = green;
      image.data[at + 2] = blue;
      image.data[at + 3] = 255;
    }
  }

  context.putImageData(image, 0, 0);
  return painted;
}

/**
 * Where a tile lands on a painted region, in that canvas's own pixels.
 *
 * Both maps and the editor need this and all three had their own copy of the
 * subtraction, which is the sort of thing that is right until a region does not
 * start at nought.
 */
export function tileOnMap(
  region: Pick<LocalRegion, 'originX' | 'originZ' | 'width' | 'height'>,
  tx: number,
  tz: number,
): { x: number; y: number } {
  return { x: tx - region.originX, y: tz - region.originZ };
}

/**
 * The same, for somewhere between tiles.
 *
 * A character halfway across a tile is halfway across its pixel, which is what
 * keeps a dot moving smoothly rather than hopping a whole square at a time. The
 * middle of tile n comes out at the middle of pixel n, so the two agree.
 */
export function pointOnMap(
  region: Pick<LocalRegion, 'originX' | 'originZ' | 'width' | 'height'>,
  x: number,
  z: number,
): { x: number; y: number } {
  return { x: x - region.originX, y: z - region.originZ };
}

/** A rectangle in the one space every region shares. */
export interface Placed {
  originX: number;
  originZ: number;
  width: number;
  height: number;
}

/**
 * The ground every region between them covers.
 *
 * Empty regions, the ones with no size on them, are left out: a file that could
 * not be read should not drag the world out to the origin.
 */
export function worldBounds(regions: readonly Placed[]): Placed {
  const held = regions.filter((one) => one.width > 0 && one.height > 0);
  if (held.length === 0) return { originX: 0, originZ: 0, width: 1, height: 1 };

  const left = Math.min(...held.map((one) => one.originX));
  const top = Math.min(...held.map((one) => one.originZ));
  const right = Math.max(...held.map((one) => one.originX + one.width));
  const bottom = Math.max(...held.map((one) => one.originZ + one.height));

  return { originX: left, originZ: top, width: right - left, height: bottom - top };
}

/**
 * Somewhere in the world, in the pixels a map of all of it is drawn in.
 *
 * The same as a single region: north up, east right. Written once here so a map
 * of everywhere and a map of one place can never disagree about which way round
 * the world is.
 */
export function pointOnWorld(bounds: Placed, x: number, z: number): { x: number; y: number } {
  return { x: x - bounds.originX, y: z - bounds.originZ };
}

/** Where one region's rectangle lands on that map, in the same pixels. */
export function rectOnWorld(
  bounds: Placed,
  region: Placed,
): { x: number; y: number; width: number; height: number } {
  const corner = pointOnWorld(bounds, region.originX, region.originZ);

  return { x: corner.x, y: corner.y, width: region.width, height: region.height };
}

/**
 * A chart drawn as a picture, one pixel to the reading.
 *
 * The whole of a place somebody has never been: coarse, cheap, and enough to
 * see a coastline, a mountain range and where the rivers run. Drawn at the
 * chart's own size and stretched to fit wherever it belongs, which is why the
 * step is not needed here.
 */
export function paintChart(
  chart: {
    width: number;
    height: number;
    kinds: ArrayLike<number>;
    heights: ArrayLike<number>;
  },
  /**
   * Whether the open sea in this chart is left clear.
   *
   * A place standing inside another place is drawn over it, and a small island
   * in the middle of an ocean carries a rectangle of its own ocean around it.
   * Two oceans at slightly different depths are two different blues, so the
   * island came with a pale blue box drawn round it. Left clear, the sea the
   * island is actually in shows through and the island sits in it.
   *
   * Only the open sea, and only where it is deep: the shallows round the island
   * are the island's own, and they are what makes it read as an island rather
   * than as a shape cut out of the water.
   */
  clearSea = false,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, chart.width);
  canvas.height = Math.max(1, chart.height);

  const context = canvas.getContext('2d');
  if (!context) return canvas;

  const image = context.createImageData(canvas.width, canvas.height);
  const span = CHART_CEILING - CHART_FLOOR;

  for (let at = 0; at < chart.width * chart.height; at++) {
    const kind = chart.kinds[at] ?? 0;
    const height = ((chart.heights[at] ?? 0) / (CHART_LEVELS - 1)) * span + CHART_FLOOR;
    const wet = (kind & CHART_WET) !== 0;

    const made = kind & 0x0f;
    const [red, green, blue] = colourOf(wet ? WATER : 0, height, wet && made !== LAVA ? 0 : made);

    image.data[at * 4] = red;
    image.data[at * 4 + 1] = green;
    image.data[at * 4 + 2] = blue;
    image.data[at * 4 + 3] = clearSea && wet && made !== LAVA && height < -1.5 ? 0 : 255;
  }

  context.putImageData(image, 0, 0);
  return canvas;
}
